import { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, today } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Play, Download } from "lucide-react";

const AS_OF_PRESETS = [
    { v: "today", l: "Today" },
    { v: "this_month_end", l: "End of This Month" },
    { v: "previous_month_end", l: "End of Previous Month" },
    { v: "fy_end", l: "End of This FY (Mar 31)" },
    { v: "previous_fy_end", l: "End of Previous FY" },
    { v: "custom", l: "Custom Date" },
];

function asOfFromPreset(preset, custom) {
    const t = new Date(today() + "T00:00:00");
    const fmt = (d) => d.toISOString().slice(0, 10);
    switch (preset) {
        case "today": return fmt(t);
        case "this_month_end": return fmt(new Date(t.getFullYear(), t.getMonth() + 1, 0));
        case "previous_month_end": return fmt(new Date(t.getFullYear(), t.getMonth(), 0));
        case "fy_end": {
            const y = t.getMonth() < 3 ? t.getFullYear() : t.getFullYear() + 1;
            return fmt(new Date(y, 2, 31));
        }
        case "previous_fy_end": {
            const y = t.getMonth() < 3 ? t.getFullYear() - 1 : t.getFullYear();
            return fmt(new Date(y, 2, 31));
        }
        case "custom": return custom || today();
        default: return today();
    }
}

function downloadCsv(rows, partyKey, partyLabel, asOf, fileBase) {
    const headers = [partyLabel, "Current", "1-30", "31-60", "61-90", "90+", "Total"];
    const escape = (v) => {
        const s = String(v ?? "");
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(",")];
    rows.forEach((r) => {
        lines.push([r[partyKey], r.current, r.d1_30, r.d31_60, r.d61_90, r.d90_plus, r.total].map(escape).join(","));
    });
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${fileBase}_${asOf}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
}

export function AgingBase({ url, kicker, title, subtitle, partyKey, partyLabel, fileBase, testIdPrefix }) {
    const [preset, setPreset] = useState("today");
    const [customAsOf, setCustomAsOf] = useState(today());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const asOf = useMemo(() => asOfFromPreset(preset, customAsOf), [preset, customAsOf]);
    const isCustom = preset === "custom";

    const run = async () => {
        setLoading(true);
        try { const { data } = await api.get(url, { params: { as_of: asOf } }); setData(data); }
        finally { setLoading(false); }
    };
    useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

    return (
        <>
            <PageHeader kicker={kicker} title={title} subtitle={subtitle} />
            <PageBody>
                <div className="bg-card border border-border rounded-md p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="report-filters">
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">As of</Label>
                        <Select value={preset} onValueChange={setPreset}>
                            <SelectTrigger className="h-9 w-[210px]" data-testid={`${testIdPrefix}-preset`}>
                                <Calendar className="w-3.5 h-3.5 mr-1.5 text-muted-foreground" />
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {AS_OF_PRESETS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    {isCustom && (
                        <div className="flex flex-col gap-1.5">
                            <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Date</Label>
                            <Input type="date" value={customAsOf} onChange={(e) => setCustomAsOf(e.target.value)} className="h-9 w-[160px]" data-testid={`${testIdPrefix}-asof`} />
                        </div>
                    )}
                    {!isCustom && (
                        <div className="flex flex-col gap-1.5 text-[11.5px] text-muted-foreground self-end pb-1.5">{asOf}</div>
                    )}
                    <div className="ml-auto flex items-center gap-2">
                        <Button variant="outline" size="sm" disabled={!data} onClick={() => downloadCsv(data.rows || [], partyKey, partyLabel, asOf, fileBase)} data-testid={`${testIdPrefix}-export-csv`}>
                            <Download className="w-3.5 h-3.5 mr-1" />CSV
                        </Button>
                        <Button size="sm" onClick={run} data-testid={`${testIdPrefix}-run`}><Play className="w-3.5 h-3.5 mr-1.5" />Run Report</Button>
                    </div>
                </div>
                {loading && <div className="text-center text-muted-foreground py-12">Loading…</div>}
                {data && !loading && (
                    <div className="bg-card border border-border rounded-md overflow-hidden" data-testid={`${testIdPrefix}-report`}>
                        <table className="fin-table">
                            <thead><tr><th>{partyLabel}</th><th className="num">Current</th><th className="num">1–30</th><th className="num">31–60</th><th className="num">61–90</th><th className="num">90+</th><th className="num">Total</th></tr></thead>
                            <tbody>
                                {data.rows.map((r, i) => (
                                    <tr key={i}>
                                        <td className="font-medium">{r[partyKey]}</td>
                                        <td className="num">{fmtMoney(r.current)}</td>
                                        <td className="num">{fmtMoney(r.d1_30)}</td>
                                        <td className="num">{fmtMoney(r.d31_60)}</td>
                                        <td className="num">{fmtMoney(r.d61_90)}</td>
                                        <td className="num text-destructive">{fmtMoney(r.d90_plus)}</td>
                                        <td className="num font-medium">{fmtMoney(r.total)}</td>
                                    </tr>
                                ))}
                                {data.rows.length === 0 && (
                                    <tr><td colSpan={7} className="text-center text-muted-foreground py-8">No outstanding balances as of {asOf}.</td></tr>
                                )}
                                <tr className="bg-muted/40 font-medium">
                                    <td>Total</td>
                                    <td className="num">{fmtMoney(data.totals.current)}</td>
                                    <td className="num">{fmtMoney(data.totals.d1_30)}</td>
                                    <td className="num">{fmtMoney(data.totals.d31_60)}</td>
                                    <td className="num">{fmtMoney(data.totals.d61_90)}</td>
                                    <td className="num">{fmtMoney(data.totals.d90_plus)}</td>
                                    <td className="num text-[15px]">{fmtMoney(data.totals.total)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </PageBody>
        </>
    );
}

export default function ARAging() {
    return <AgingBase
        url="/reports/ar-aging"
        kicker="REPORTS • RECEIVABLES"
        title="AR Aging"
        subtitle="Outstanding receivables by aging bucket."
        partyKey="customer_name"
        partyLabel="Customer"
        fileBase="AR_Aging"
        testIdPrefix="ar"
    />;
}
